/**
 * Formats file size in bytes to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Downloads a file with the given content and filename
 * @param {ArrayBuffer} content - File content as ArrayBuffer
 * @param {string} filename - Name of the file to download
 * @param {string} mimeType - MIME type of the file
 */
export const downloadFile = (content, filename, mimeType) => {
  try {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke the URL after a delay to ensure download starts properly
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error('Failed to download file');
  }
};

/**
 * Downloads JSON data as a file
 * @param {object} data - JSON data to download
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadJson = (data, filename) => {
  try {
    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9-_]/g, '');
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizedFilename}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke the URL after a delay to ensure download starts properly
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error downloading JSON:', error);
    throw new Error('Failed to download JSON file');
  }
};

/**
 * Ricava il nome del file dall'header Content-Disposition (`filename*=UTF-8''x.csv`, `filename="x.csv"` o `filename=x.csv`);
 * senza header o senza filename restituisce `fallback`. Il nome dell'header e' case-insensitive.
 * @param {object} headers - Header della risposta axios
 * @param {string} fallback - Nome da usare se l'header manca
 * @returns {string}
 */
export const fileNameFromContentDisposition = (headers, fallback) => {
  const key = headers && Object.keys(headers).find((name) => name.toLowerCase() === 'content-disposition');
  const intestazione = key ? headers[key] : null;
  if (!intestazione) {
    return fallback;
  }
  const esteso = /filename\*=utf-8''([^;]+)/i.exec(intestazione);
  if (esteso) {
    try {
      return decodeURIComponent(esteso[1].trim());
    } catch (decodeError) {
      // percent-encoding malformato: si ripiega sul filename semplice
    }
  }
  const corrispondenza = /filename="?([^";]+)"?/i.exec(intestazione);
  return (corrispondenza && corrispondenza[1]) || fallback;
};

/**
 * Legge il `message` di un errore la cui risposta e' un blob (richieste con responseType 'blob':
 * il corpo JSON dell'errore arriva come Blob e va riletto). Restituisce null se non leggibile.
 * @param {object} error - Errore axios
 * @returns {Promise<string|null>}
 */
export const readBlobError = async (error) => {
  const data = error && error.response && error.response.data;
  if (!data || typeof data.text !== 'function') {
    return null;
  }
  try {
    const corpo = JSON.parse(await data.text());
    return (corpo && corpo.message) || null;
  } catch (parseError) {
    return null;
  }
}; 